// =============================================================================
// MakeBookingV2.tsx — create / edit a booking, Bookings V2 chrome.
//
// This is a presentation-only restyle of MakeBooking: every handler, effect and
// save/cancel rule is identical to the V1 component (kept verbatim so behaviour
// can't drift between the two screens). What changed is the modal's look — it now
// adopts the Member Profile V2 ("Fortius") language:
//   - the shared <ConfigProvider theme={fortiusProfileTheme}> (teal primary)
//   - a teal eyebrow + bold system-font title (no serif) with a 4px teal top
//     accent bar and the payment tag pinned top-right
//   - a 4-up meta strip (Court · Time · Party · Total) on the tint surface
//   - everything else stays on the existing Ant field building blocks
// All bespoke colour/type values come from fortius-bookings.css / bookingTokens.ts
// (the FORT-178 seam), not inline literals.
// =============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Modal, Alert, Divider, Checkbox, Input, ConfigProvider } from 'antd';
import moment from 'moment-timezone';
import { Slot } from '../../../model/bookings/Slot';
import { Booking } from '../../../model/bookings/Booking';
import { CreateBookingRequest } from '../../../model/bookings/CreateBookingRequest';
import { CancelBookingRequest } from '../../../model/bookings/CancelBookingRequest';
import { PlayingMember } from '../../../model/members/PlayingMember';
import { ApiError } from '../../../common/api/ClubBookingApiClient';
import {
  Facility,
  getScheduleForDay,
  getDefaultBookingLengthMins,
} from '../../../model/facilities/Facility';
import { AlertMessage } from '../../common/AlertMessage';
import { useBookingService } from '../../../services/bookings/BookingServiceProvider';
import { useClient } from '../../../common/ClientProvider';
import { useLoading } from '../../../common/loading/LoadingContext';
import { useMember } from '../../../common/member/MemberContext';
import { useFormatCurrency, useCurrencySymbol } from '../../../hooks/useFormatters';

import BookingMemberSelector from '../BookingMemberSelector';
import AdditionalMemberSelector from '../AdditionalMemberSelector';
import BookingActionButtons from '../BookingActionButtons';
import CancelConfirmationModal from '../CancelConfirmationModal';
import StartEndTimeSelector from '../StartEndTimeSelector';
import CheckInBooking from '../confirmations/CheckInBooking';
import { convertToUtc, formatInTimezone } from '../../../utils/TimezoneUtils';
import { CreatePointOfSaleTransactionRequest } from '../../../model/pointofsale/CreatePointOfSaleTransactionRequest';
import { PointOfSaleTransaction } from '../../../model/pointofsale/PointOfSaleTransaction';
import { usePointOfSaleService } from '../../../services/pointofsale/PointOfSaleServiceProvider';
import { UpdatePaymentTransactionRequest } from '../../../model/pointofsale/UpdatePaymentTransactionRequest';

import { fortiusBookingsTheme } from './bookingsV2Theme';
import { derivePaymentTag } from './bookingStatus';
import { isAdminBooking as detectAdminBooking } from '../bookingPaymentStatus';
import { isNonRegularMember, canPlaceAdminBooking, resolveAdminBookingMember } from './bookingAccess';
import { paymentTagPalette } from './bookingTokens';
import './fortius-bookings.css';

interface MakeBookingV2Props {
  slot: Slot;
  booking: Booking | null;
  onClose: (wasSuccessful: boolean) => void;
  facility: Facility;
  members: PlayingMember[];
}

function createDefaultBooking(court: string, startTime: Date, facility: Facility): Booking {
  const endTime = new Date(startTime);
  // Default the end time using the facility's configured DefaultBookingLengthMins
  // metadata (falling back to 60 mins for Squash / one interval otherwise).
  const defaultBookingLengthMins = getDefaultBookingLengthMins(facility);
  endTime.setMinutes(endTime.getMinutes() + defaultBookingLengthMins);

  return {
    bookingId: 0,
    bookingDateTimeUtc: startTime,
    bookingEndTimeUtc: endTime,
    facilityName: court,
    bookingStatus: 'Available',
    paymentTransactionStatus: 'Unpaid',
    memberName: '',
    membershipId: '',
    playingMembers: [],
  };
}

const MakeBookingV2: React.FC<MakeBookingV2Props> = ({
  slot,
  booking: initialBooking,
  onClose,
  facility,
  members,
}) => {
  // Services and context hooks
  const { currentClientId } = useClient();
  const bookingService = useBookingService();
  const { withLoading } = useLoading();
  const { currentMember, permissions, isAdmin } = useMember();
  // Coaches (and any non-Member member type) book like staff for time-slot
  // selection: they get the full end-time range instead of the regular-member
  // two-interval cap, so they can book longer far-ahead coaching sessions. This
  // is deliberately limited to slot selection — management actions (check-in,
  // club/admin bookings, on-behalf member selection, price override) stay
  // admin-only, matching the booking-only Coach ceiling. [FORT-278]
  const canSelectFullBookingRange = isNonRegularMember(isAdmin, currentMember?.memberType);
  // Admin bookings (member-less, titled — e.g. club coaching) are open to staff and
  // to coaches holding bookings:coach:<facilityType> for the slot's sport. This gate
  // surfaces the toggle/title/price in the UI; the server enforces the same rule in
  // BookingValidationService (an admin-booking shape is rejected unless the requester
  // is staff or a coach for the facility type), so it isn't bypassable. [FORT-278]
  const canAdminBook = canPlaceAdminBooking(
    isAdmin,
    permissions.permissions.map((p) => p.name),
    slot.facilityType
  );
  const [transaction, setTransaction] = useState<PointOfSaleTransaction | null>(null);
  const pointOfSaleService = usePointOfSaleService();
  const formatCurrency = useFormatCurrency();
  const currencySymbol = useCurrencySymbol();

  // Get price from slot metadata first, fallback to facility metadata
  const getPrice = (): number => {
    const slotPrice = slot?.metadata['Price'];
    const facilityPrice = facility.metadata['Price'];

    if (slotPrice !== undefined && slotPrice !== null) {
      const parsedSlotPrice = typeof slotPrice === 'string' ? parseFloat(slotPrice) : Number(slotPrice);
      if (!isNaN(parsedSlotPrice)) {
        return parsedSlotPrice;
      }
    }

    if (facilityPrice !== undefined && facilityPrice !== null) {
      const parsedFacilityPrice =
        typeof facilityPrice === 'string' ? parseFloat(facilityPrice) : Number(facilityPrice);
      if (!isNaN(parsedFacilityPrice)) {
        return parsedFacilityPrice;
      }
    }

    return 0;
  };

  const price = getPrice();

  // State variables
  const [booking, setBooking] = useState<Booking>(
    initialBooking || createDefaultBooking(facility.name, slot.startTime, facility)
  );
  const initialBookingRef = useRef<Booking>(
    initialBooking || createDefaultBooking(facility.name, slot.startTime, facility)
  );
  const [mainBookingMember, setMainBookingMember] = useState<PlayingMember | null>(null);
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false);
  const [bookingLengthMins, setBookingLengthMins] = useState(
    moment(booking.bookingEndTimeUtc).diff(moment(booking.bookingDateTimeUtc), 'minutes') ||
      getDefaultBookingLengthMins(facility)
  );
  const initialBookingLengthMins = useRef(bookingLengthMins);
  const [message, setMessage] = useState<AlertMessage | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [timeValidationMessage, setTimeValidationMessage] = useState<string | null>(null);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [isCheckInModalVisible, setIsCheckInModalVisible] = useState(false);
  // Start time is fixed to the clicked slot (the start-time selector is not
  // surfaced in this flow — same effective behaviour as V1, where it was disabled).
  const [startTime] = useState<Date>(booking.bookingDateTimeUtc);
  const [endTime, setEndTime] = useState<Date>(booking.bookingEndTimeUtc);
  // Detect a reopened admin booking by its metadata (NonMembers / BookingTitle), not
  // the member name — a coach's admin booking is attributed to the coach, so a
  // name==client check would misread it as a personal booking and drop the title on
  // save. [FORT-278]
  const initialIsAdminBooking = initialBooking
    ? detectAdminBooking(slot, initialBooking, currentClientId)
    : !!slot.metadata?.BookingTitle;
  const savedAdminPriceRaw = initialBooking?.metadata?.Price;
  const savedAdminPrice =
    typeof savedAdminPriceRaw === 'string'
      ? parseFloat(savedAdminPriceRaw)
      : typeof savedAdminPriceRaw === 'number'
        ? savedAdminPriceRaw
        : NaN;
  const initialPrice = initialIsAdminBooking
    ? initialBooking && !isNaN(savedAdminPrice)
      ? savedAdminPrice
      : 0.0
    : price ?? 0.0;
  const [currentPrice, setCurrentPrice] = useState<number>(initialPrice);
  const [priceInputValue, setPriceInputValue] = useState<string>(initialPrice.toString());
  const [isAdminBooking, setIsAdminBooking] = useState(initialIsAdminBooking);
  const [adminBookingTitle, setAdminBookingTitle] = useState(
    (initialBooking?.metadata?.BookingTitle as string) ||
      (slot.metadata?.BookingTitle as string) ||
      ''
  );
  const [previousBooking, setPreviousBooking] = useState<Booking | null>(initialBooking);
  const hasInitializedEndTime = useRef(false);

  const showCheckIn =
    isAdmin &&
    ((facility.metadata['CheckinEnabled'] as boolean) ?? false) &&
    booking.bookingStatus === 'Booked' &&
    booking.paymentTransactionStatus !== 'Completed';

  const additionalMembers =
    (facility.metadata['FacilityBookingType'] as string) === 'MultiMemberBooking';

  // Generate end time options. Anchored in the FACILITY timezone (slot.timezone),
  // not the operator's browser timezone, so the wall-clock labels and the
  // resulting UTC instants are correct everywhere and DST-safe (moment-timezone
  // arithmetic). Options step by bookingIntervalMins.
  const generateEndTimeOptions = () => {
    const options: { label: string; value: string }[] = [];
    const schedule = getScheduleForDay(facility, new Date(slot.startTime));
    if (!schedule) return options;

    const tz = slot.timezone || facility.timezone;
    const intervalMins = facility.bookingIntervalMins;
    const startMoment = moment(slot.startTime).tz(tz);
    const dateYmd = startMoment.format('YYYY-MM-DD');
    const scheduleEnd = moment.tz(`${dateYmd} ${schedule.endTime}`, 'YYYY-MM-DD HH:mm', tz);

    const pushOption = (m: moment.Moment) => {
      options.push({ label: m.format('HH:mm'), value: m.toDate().toISOString() });
    };

    if (canSelectFullBookingRange) {
      let currentTime = startMoment.clone().add(intervalMins, 'minutes');
      while (currentTime.isSameOrBefore(scheduleEnd)) {
        pushOption(currentTime);
        currentTime = currentTime.clone().add(intervalMins, 'minutes');
      }
    } else {
      // Regular members are limited to the next two intervals.
      pushOption(startMoment.clone().add(intervalMins, 'minutes'));
      pushOption(startMoment.clone().add(intervalMins * 2, 'minutes'));
    }

    return options;
  };

  const endTimeOptions = generateEndTimeOptions();

  // Generate start time options (admin only), anchored in the facility timezone.
  const generateStartTimeOptions = () => {
    const options: { label: string; value: string }[] = [];
    const schedule = getScheduleForDay(facility, new Date(slot.startTime));
    if (!schedule || !isAdmin) return options;

    const tz = slot.timezone || facility.timezone;
    const intervalMins = facility.bookingIntervalMins;
    const dateYmd = moment(slot.startTime).tz(tz).format('YYYY-MM-DD');
    const scheduleStart = moment.tz(`${dateYmd} ${schedule.startTime}`, 'YYYY-MM-DD HH:mm', tz);
    const scheduleEnd = moment.tz(`${dateYmd} ${schedule.endTime}`, 'YYYY-MM-DD HH:mm', tz);

    // A start must leave room for at least one interval before close.
    let currentTime = scheduleStart.clone();
    while (currentTime.clone().add(intervalMins, 'minutes').isSameOrBefore(scheduleEnd)) {
      options.push({ label: currentTime.format('HH:mm'), value: currentTime.toDate().toISOString() });
      currentTime = currentTime.clone().add(intervalMins, 'minutes');
    }

    return options;
  };

  const startTimeOptions = generateStartTimeOptions();

  // Set default end time for non-admin users (only once on initial mount, and only for new bookings)
  useEffect(() => {
    if (!isAdmin && endTimeOptions.length > 0 && !hasInitializedEndTime.current && booking.bookingId === 0) {
      const defaultLengthMins = getDefaultBookingLengthMins(facility);
      const targetEndTime = moment(slot.startTime).add(defaultLengthMins, 'minutes');
      const matchingOption = endTimeOptions.find((option) =>
        moment(option.value).isSame(targetEndTime, 'minute')
      );
      const fallbackOption = endTimeOptions[1] ?? endTimeOptions[0];
      const defaultEndTime = new Date((matchingOption ?? fallbackOption).value);
      setEndTime(defaultEndTime);
      setBooking((prev) => ({
        ...prev,
        bookingEndTimeUtc: defaultEndTime,
      }));
      setBookingLengthMins(moment(defaultEndTime).diff(moment(slot.startTime), 'minutes'));
      hasInitializedEndTime.current = true;
    }
  }, [isAdmin, endTimeOptions, facility, slot.startTime, booking.bookingId]);

  // False until an existing booking's payment has loaded; a new booking has none to wait for.
  const [transactionLoaded, setTransactionLoaded] = useState(booking.bookingId === 0);
  useEffect(() => {
    const loadTransactionForBooking = async () => {
      const transactions = await pointOfSaleService.getTransactionsForBooking(booking.bookingId);
      setTransaction(transactions?.[0]);
      setTransactionLoaded(true);
    };
    if (booking.bookingId !== 0) {
      loadTransactionForBooking();
    }
  }, [booking.bookingId, pointOfSaleService]);

  // Set error message with timeout
  const setErrorMessage = (error: string) => {
    setMessage({ type: 'error', text: error });
    setShowAlert(true);

    setTimeout(() => {
      setShowAlert(false);
    }, 5000);
  };

  // Check if booking has changes
  const hasBookingChanges = (
    currentBooking: Booking,
    initialBooking: Booking,
    currentLength: number,
    initialLength: number
  ): boolean => {
    if (!isAdmin && !isAdminBooking) {
      return !!currentBooking.membershipId;
    }

    const initialPrice = price ?? 0.0;
    const priceChanged = Math.abs(currentPrice - initialPrice) > 0.01;

    const baseChanges =
      currentBooking.memberName !== initialBooking.memberName ||
      currentBooking.membershipId !== initialBooking.membershipId ||
      currentBooking.paymentTransactionStatus !== initialBooking.paymentTransactionStatus ||
      currentLength !== initialLength ||
      currentBooking.bookingDateTimeUtc !== initialBooking.bookingDateTimeUtc ||
      currentBooking.bookingEndTimeUtc !== initialBooking.bookingEndTimeUtc ||
      currentBooking.metadata !== initialBooking.metadata ||
      adminBookingTitle !== initialBooking.metadata?.BookingTitle ||
      priceChanged;

    if (facility.metadata['FacilityBookingType'] === 'MultiMemberBooking') {
      return (
        baseChanges ||
        currentBooking.playingMembers.length !== initialBooking.playingMembers.length ||
        !currentBooking.playingMembers.every(
          (member) =>
            initialBooking.playingMembers.includes(member) &&
            currentBooking.playingMembers.filter((m) => m === member).length === 1
        )
      );
    }

    return baseChanges;
  };

  // Effect to update save button state and set main member
  useEffect(() => {
    setTimeValidationMessage(null);
    setIsSaveButtonEnabled(
      hasBookingChanges(
        booking,
        initialBookingRef.current,
        bookingLengthMins,
        initialBookingLengthMins.current
      )
    );

    if (isAdminBooking) {
      const adminMember = getAdminMember();
      if (adminMember) {
        setMainBookingMember(adminMember);
        setBooking((prev) => ({
          ...prev,
          memberName: adminMember.name,
          membershipId: adminMember.membershipId,
        }));
      }
    } else if (!isAdmin) {
      const nonAdminMember =
        members?.find((m) => m.membershipId === currentMember?.membershipId) ?? null;

      setMainBookingMember(nonAdminMember);

      setBooking((prev) => ({
        ...prev,
        memberName: nonAdminMember?.name ?? prev.memberName ?? currentMember?.name ?? '',
        membershipId:
          nonAdminMember?.membershipId ?? prev.membershipId ?? currentMember?.membershipId ?? '',
      }));
    } else {
      setMainBookingMember(members?.find((m) => m.name === booking.memberName) ?? null);
    }
    // hasBookingChanges is redefined each render; including it would re-run this
    // effect on every render. The recompute stays keyed on the booking state above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentMember,
    isAdmin,
    members,
    booking,
    bookingLengthMins,
    isAdminBooking,
    currentClientId,
    startTime,
    endTime,
  ]);

  // The booking's price comes from the API, re-quoted whenever its length changes —
  // the browser never calculates a price. [FORT-288]
  const [priceQuoteState, setPriceQuoteState] = useState<'idle' | 'pending' | 'failed'>('idle');
  useEffect(() => {
    if (isAdminBooking) {
      // Staff set an admin booking's price by hand.
      setPriceQuoteState('idle');
      return;
    }
    const slotPrice = slot?.metadata['Price'];
    const isUsingSlotPriceOverride = slotPrice !== undefined && slotPrice !== null;

    if (isUsingSlotPriceOverride) {
      // A price stored on the slot's booking is a fixed price, not a calculation.
      setPriceQuoteState('idle');
      setCurrentPrice(price > 0 ? Number(price.toFixed(2)) : 0);
      return;
    }

    // An existing booking keeps the price it was booked at (recorded on its payment)
    // unless its length changes — reopening it must not re-price it at today's rates.
    const isExistingBooking = booking.bookingId > 0;
    const lengthUnchanged =
      startTime.getTime() === initialBookingRef.current.bookingDateTimeUtc.getTime() &&
      endTime.getTime() === initialBookingRef.current.bookingEndTimeUtc.getTime();
    if (isExistingBooking && lengthUnchanged) {
      if (!transactionLoaded) {
        setPriceQuoteState('pending');
        return;
      }
      if (transaction && transaction.bookingSlots?.length > 0) {
        setCurrentPrice(transaction.bookingSlots.reduce((sum, s) => sum + (s.price ?? 0), 0));
        setPriceQuoteState('idle');
        return;
      }
    }

    let cancelled = false;
    setPriceQuoteState('pending');
    (async () => {
      const quote = await bookingService.getPriceQuote(facility.name, startTime, endTime);
      if (cancelled) return;
      if (quote) {
        setCurrentPrice(quote.price);
        setPriceQuoteState('idle');
      } else {
        // Never fall back to a guessed or zero price: saving waits for a real quote.
        setPriceQuoteState('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    endTime,
    startTime,
    facility.name,
    price,
    slot?.metadata,
    isAdminBooking,
    bookingService,
    booking.bookingId,
    transaction,
    transactionLoaded,
  ]);

  // Event handlers
  const handleCheckInClose = (wasSuccessful?: boolean) => {
    setIsCheckInModalVisible(false);
    if (wasSuccessful) {
      onClose(true);
    }
  };

  const handleEndTimeChange = (value: string) => {
    const newEndTime = new Date(value);
    setEndTime(newEndTime);
    setBooking((prev) => ({
      ...prev,
      bookingEndTimeUtc: newEndTime,
    }));

    const newLengthMins = moment(newEndTime).diff(moment(startTime), 'minutes');
    setBookingLengthMins(newLengthMins);
    setIsSaveButtonEnabled(true);
  };

  const handleMainMemberChange = (membershipId: string) => {
    const member = members?.find((m) => m.membershipId === membershipId);

    if (member) {
      setMainBookingMember(member);
      setBooking((prev) => ({
        ...prev,
        memberName: member.name,
        membershipId: member.membershipId,
      }));

      setIsSaveButtonEnabled(true);
    }
  };

  const handlePlayingMembersChange = (membershipIds: string[]) => {
    const selectedMembers = membershipIds
      .map((id) => members?.find((m) => m.membershipId === id))
      .filter((m): m is PlayingMember => m !== undefined);

    setBooking((prev) => ({
      ...prev,
      playingMembers: selectedMembers.map((m) => m.name),
    }));
    setIsSaveButtonEnabled(true);
  };

  const handleRemovePlayingMember = (memberToRemove: string) => {
    setBooking((prev) => ({
      ...prev,
      playingMembers: prev.playingMembers.filter((name) => name !== memberToRemove),
    }));
    setIsSaveButtonEnabled(true);
  };

  const handleAdminBookingChange = (checked: boolean) => {
    setIsAdminBooking(checked);
    if (checked) {
      setMainBookingMember(null);
      const adminMember = getAdminMember();
      setPreviousBooking(booking);
      setBooking((prev) => ({
        ...prev,
        memberName: adminMember?.name ?? '',
        membershipId: adminMember?.membershipId ?? '',
        playingMembers: [],
      }));
      setCurrentPrice(0.0);
      setPriceInputValue('0');
    } else {
      setBooking(previousBooking ?? booking);
      setCurrentPrice(price ?? 0.0);
      setPriceInputValue((price ?? 0.0).toString());
    }
    setIsSaveButtonEnabled(true);
  };

  const getAdminMember = () =>
    resolveAdminBookingMember(isAdmin, members, currentClientId, currentMember?.membershipId);

  const handleAdminBookingTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdminBookingTitle(e.target.value);
    setIsSaveButtonEnabled(true);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      setPriceInputValue('');
      setCurrentPrice(0);
      setIsSaveButtonEnabled(true);
      return;
    }

    if (!/^[\d.]*$/.test(value)) {
      return;
    }

    if ((value.match(/\./g) || []).length > 1) {
      return;
    }

    setPriceInputValue(value);

    if (value.endsWith('.') || value === '.') {
      setIsSaveButtonEnabled(true);
      return;
    }

    const newPrice = parseFloat(value) || 0;
    setCurrentPrice(newPrice);
    setIsSaveButtonEnabled(true);
  };

  const handleOpenCancelModal = () => {
    setIsCancelModalVisible(true);
  };

  const createBookingPaymentRequest = (price: number): CreatePointOfSaleTransactionRequest => {
    return {
      membershipId: booking.membershipId,
      items: [],
      amount: price,
      bookingSlots: [
        {
          bookingSlot: slot,
          price: price,
        },
      ],
      paymentTransactionStatus: 'Pending',
    };
  };

  const handleCancelBooking = async () => {
    try {
      if (!isAdmin && booking.membershipId !== currentMember?.membershipId) {
        setErrorMessage('You can only cancel your own bookings.');
        setIsCancelModalVisible(false);
        onClose(false);
        return;
      }

      if (!booking.membershipId || !booking.memberName) {
        console.error('Booking missing member information:', booking);
        setErrorMessage('Booking information is incomplete. Please try again.');
        setIsCancelModalVisible(false);
        onClose(false);
        return;
      }

      const cancelBookingRequest: CancelBookingRequest = {
        bookingDateTimeUtc: booking.bookingDateTimeUtc,
        facilityName: booking.facilityName,
        membershipId: booking.membershipId,
        requestingUsername: booking.memberName,
        bookingId: booking.bookingId,
      };

      await withLoading(bookingService.cancelBooking(cancelBookingRequest));
      onClose(true);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      if (error instanceof ApiError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred while cancelling the booking');
      }
      setIsCancelModalVisible(false);
      onClose(false);
    }
  };

  const handleSaveBooking = async () => {
    if (booking.bookingId > 0 && !isAdmin && booking.membershipId !== currentMember?.membershipId) {
      setErrorMessage('You can only edit your own bookings.');
      return;
    }

    let bookingStartTime = startTime;
    let bookingEndTime = endTime;

    if (!isAdmin || startTimeOptions.length === 0) {
      bookingStartTime = new Date(slot.startTime);
    }
    if (!isAdmin || endTimeOptions.length === 0) {
      bookingEndTime = new Date(slot.startTime);
      bookingEndTime.setMinutes(bookingEndTime.getMinutes() + bookingLengthMins);
    }

    if (!isAdminBooking && booking.playingMembers.length > 0 && !mainBookingMember) {
      setErrorMessage('Main booking member has not been selected');
      return;
    }

    try {
      let mainMember: PlayingMember | null = null;
      if (!isAdminBooking) {
        if (!mainBookingMember) {
          setErrorMessage('Main booking member has not been selected');
          return;
        }
        mainMember = mainBookingMember;
      } else {
        const adminMember = getAdminMember();
        if (!adminMember) {
          setErrorMessage('Could not find the member to attribute this booking to');
          return;
        }
        mainMember = adminMember;
      }

      let playingMembers: PlayingMember[] = [];

      if (!isAdminBooking && facility.metadata['FacilityBookingType'] === 'MultiMemberBooking') {
        playingMembers = booking.playingMembers
          .map((name) => members?.find((m) => m.name === name))
          .filter((m): m is PlayingMember => m !== undefined);

        if (mainMember && !playingMembers.some((m) => m.membershipId === mainMember?.membershipId)) {
          playingMembers.push(mainMember);
        }
      } else {
        playingMembers = mainMember ? [mainMember] : [];
      }

      if (currentClientId === undefined) {
        throw new Error('Current client ID is undefined');
      }

      const startTimeUtc = convertToUtc(bookingStartTime, slot.timezone);
      const endTimeUtc = convertToUtc(bookingEndTime, slot.timezone);

      const createBookingRequest: CreateBookingRequest = {
        bookingId: booking.bookingId,
        slot: slot,
        startTime: startTimeUtc,
        endTime: endTimeUtc,
        mainBookingMember: mainMember,
        facilityName: booking.facilityName,
        selectedMembers: playingMembers,
        bookingPaymentRequest: null,
        bookingPaymentUpdateRequest: null,
        clientName: currentClientId,
        facilityBookingType: facility.metadata['FacilityBookingType'] as string,
        metadata: isAdminBooking
          ? ({
              BookingTitle: adminBookingTitle,
              Price: currentPrice,
              NonMembers: true,
            } as unknown as Record<string, object>)
          : undefined,
        bookingOperationType: booking.bookingId !== 0 ? 'Update' : 'Create',
      };

      if (transaction) {
        const request: UpdatePaymentTransactionRequest = {
          transactionId: transaction.transactionId,
          amount: currentPrice,
          status: 'Pending',
          items: [],
          bookingSlots: [
            {
              bookingSlot: slot,
              price: currentPrice,
            },
          ],
        };
        createBookingRequest.bookingPaymentUpdateRequest = request;
      } else {
        const bookingPaymentRequest = createBookingPaymentRequest(currentPrice);
        createBookingRequest.bookingPaymentRequest = bookingPaymentRequest;
      }

      await withLoading(bookingService.saveBooking(createBookingRequest));

      setIsSaveButtonEnabled(false);
      onClose(true);
    } catch (error) {
      console.error('Error saving booking:', error);
      if (error instanceof ApiError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred while saving the booking');
      }
      onClose(false);
    }
  };

  // --- Presentation helpers (V2 chrome) --------------------------------------
  const isExistingBooking = booking.bookingId > 0;
  const eyebrow = isExistingBooking ? 'Booking' : 'New booking';
  const headerTitle =
    (isAdminBooking && adminBookingTitle) || booking.memberName || '';
  const paymentTag = isExistingBooking ? derivePaymentTag(booking, slot, currentClientId) : null;
  const partySize = booking.playingMembers.length || 1;
  const startLabel = formatInTimezone(booking.bookingDateTimeUtc, slot.timezone, 'HH:mm');

  // End-time control: the (timezone-aware, robustly-matched) selector when the
  // facility schedule yields options, else a read-only value.
  const endTimeField =
    endTimeOptions.length > 0 ? (
      <StartEndTimeSelector
        controlOnly
        timeOptions={endTimeOptions}
        selectedTime={endTime}
        labelText="End Time:"
        onChange={handleEndTimeChange}
      />
    ) : (
      <div className="bk-readonly">
        {moment(booking.bookingEndTimeUtc)
          .tz(slot.timezone || facility.timezone)
          .format('HH:mm')}
      </div>
    );

  return (
    <ConfigProvider theme={fortiusBookingsTheme}>
      <Modal
        data-testid="make-booking-v2-modal"
        open={true}
        maskClosable={false}
        closable={true}
        onCancel={() => onClose(false)}
        footer={null}
        width={520}
        centered={true}
        title={null}
        className="bk-modal"
        wrapClassName="ft-bookings-v2"
        styles={{ mask: { background: 'rgba(16, 48, 44, 0.42)', backdropFilter: 'blur(4px)' } }}
      >
        <div className="ft-bookings-v2 bk-modal-scope">
          {/* Header: teal eyebrow + bold title + payment tag */}
          <div className="bk-header">
            <div>
              <div className="bk-eyebrow">{eyebrow}</div>
              <h2 className="bk-modal-title">
                {headerTitle || <em>Untitled member</em>}
              </h2>
            </div>
            {paymentTag && (
              <span className={`bk-tag bk-tag--${paymentTag}`}>
                {paymentTagPalette[paymentTag].label}
              </span>
            )}
          </div>

          {/* Meta strip: Court · Time · Party · Total */}
          <div className="bk-meta">
            <div>
              <div className="bk-meta-label">Court</div>
              <div className="bk-meta-value">{facility.name}</div>
            </div>
            <div>
              <div className="bk-meta-label">Time</div>
              <div className="bk-meta-value">
                {startLabel} · {bookingLengthMins} min
              </div>
            </div>
            <div>
              <div className="bk-meta-label">Party</div>
              <div className="bk-meta-value">{partySize}</div>
            </div>
            <div>
              <div className="bk-meta-label">Total</div>
              <div className="bk-meta-value" data-testid="booking-v2-total">
                {priceQuoteState === 'pending'
                  ? '…'
                  : priceQuoteState === 'failed'
                    ? 'Unavailable'
                    : formatCurrency(currentPrice)}
              </div>
            </div>
          </div>

          <Divider style={{ margin: '18px 0 16px' }} />

          {/* Alert messages */}
          {message && showAlert && (
            <Alert type={message.type} message={message.text} showIcon style={{ marginBottom: 12 }} />
          )}

          <div className="bk-form">
            {/* Admin booking toggle */}
            {canAdminBook && (
              <Checkbox
                className="bk-admin-check"
                data-testid="admin-booking-checkbox"
                checked={isAdminBooking}
                onChange={(e) => handleAdminBookingChange(e.target.checked)}
              >
                Admin booking (no member)
              </Checkbox>
            )}

            {/* Booking title — admin bookings only */}
            {canAdminBook && isAdminBooking && (
              <div className="bk-field">
                <label className="bk-field-label">Booking title</label>
                <Input
                  placeholder="e.g. Club coaching, maintenance…"
                  value={adminBookingTitle}
                  onChange={handleAdminBookingTitleChange}
                />
              </div>
            )}

            {/* Booking member */}
            <div className="bk-field">
              <label className="bk-field-label">Booking member</label>
              <BookingMemberSelector
                controlOnly
                membershipId={booking.membershipId}
                onChange={handleMainMemberChange}
                isAdmin={!!isAdmin}
                memberName={
                  isAdmin || isAdminBooking
                    ? (booking.memberName ?? '')
                    : (permissions.member?.name ?? '')
                }
              />
            </div>

            {/* End time (+ editable price for admin bookings) */}
            {canAdminBook && isAdminBooking ? (
              <div className="bk-form-grid">
                <div className="bk-field">
                  <label className="bk-field-label">End time</label>
                  {endTimeField}
                </div>
                <div className="bk-field">
                  <label className="bk-field-label">Price</label>
                  <Input
                    data-testid="admin-booking-price-input"
                    value={priceInputValue}
                    onChange={handlePriceChange}
                    prefix={currencySymbol}
                    placeholder="0.00"
                  />
                </div>
              </div>
            ) : (
              <div className="bk-field">
                <label className="bk-field-label">End time</label>
                {endTimeField}
              </div>
            )}

            {/* Time validation message */}
            {timeValidationMessage && <Alert type="warning" message={timeValidationMessage} showIcon />}

            {/* Playing members — multi-member bookings */}
            {additionalMembers && (
              <div className="bk-field">
                <label className="bk-field-label">Playing members</label>
                <AdditionalMemberSelector
                  controlOnly
                  playingMembers={booking.playingMembers}
                  members={members || []}
                  membershipId={booking.membershipId}
                  onMembersChange={handlePlayingMembersChange}
                  onRemoveMember={handleRemovePlayingMember}
                />
              </div>
            )}
          </div>

          <Divider style={{ margin: '18px 0 12px' }} />

          {/* Action buttons */}
          <BookingActionButtons
            onSave={handleSaveBooking}
            onCancel={handleOpenCancelModal}
            onCheckIn={() => setIsCheckInModalVisible(true)}
            isSaveEnabled={(isSaveButtonEnabled ?? false) && priceQuoteState === 'idle'}
            showCheckIn={showCheckIn ?? false}
            isCancelEnabled={booking.bookingStatus === 'Booked'}
          />
        </div>
      </Modal>

      {/* Cancel confirmation modal */}
      <CancelConfirmationModal
        isVisible={isCancelModalVisible}
        onConfirm={handleCancelBooking}
        onCancel={() => setIsCancelModalVisible(false)}
      />

      {/* Check-in modal */}
      {facility.metadata['CheckinEnabled'] && (
        <CheckInBooking
          facility={facility}
          booking={booking}
          slot={slot}
          isVisible={isCheckInModalVisible}
          onClose={handleCheckInClose}
        />
      )}
    </ConfigProvider>
  );
};

export default MakeBookingV2;
